/* Static merchant-approved shipping policy; payment adapters remain placeholders. */
// Shopify all_country_option_tags keeps canonical English option values, even when labels are localized.
const ELIX_EU_COUNTRIES = new Set([
  'Austria', 'Belgium', 'Bulgaria', 'Croatia', 'Cyprus', 'Czech Republic', 'Czechia',
  'Denmark', 'Estonia', 'Finland', 'France', 'Germany', 'Greece', 'Hungary', 'Ireland',
  'Italy', 'Latvia', 'Lithuania', 'Luxembourg', 'Malta', 'Netherlands', 'Poland',
  'Portugal', 'Romania', 'Slovakia', 'Slovenia', 'Spain', 'Sweden',
]);
function elixStandardShipping({ address, cart, strings }) {
  // Rules are EUR amounts. Never reinterpret them as another presentment currency.
  if (cart.currency !== 'EUR') throw new Error('Static shipping requires EUR');
  const country = address.country;
  const preview = !country || country === '---';
  const price = country === 'France' ? 499 : country === 'Germany' ? 599 : ELIX_EU_COUNTRIES.has(country) ? 799 : 1299;
  return ['colissimo', 'mondial-relay', 'dhl'].map(carrier => ({ id: carrier, carrier, label: strings[`carrier_${carrier}`], detail: strings[`carrier_${carrier}_detail`], price: preview ? null : price, currency: 'EUR' }));
}
// Country-only policy is synchronous: updating a country immediately recalculates the displayed total.
elixStandardShipping.requiresAddress = false;
window.ElixOrderAdapters = window.ElixOrderAdapters || {};
if (window.ElixOrderAdapters.shipping === undefined) window.ElixOrderAdapters.shipping = elixStandardShipping;
window.ElixOrderAdapters.payments = window.ElixOrderAdapters.payments || {
  whatsapp: async () => ({ pending: true }),
  sepa: async () => ({ pending: true }),
  crypto: async () => ({ pending: true }),
};
class ElixOrder extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.strings = JSON.parse(this.querySelector('[data-order-strings]').textContent);
    this.cart = JSON.parse(this.querySelector('[data-order-cart]').textContent);
    this.form = this.querySelector('[data-order-form]');
    // Shopify includes a separator option whose value is not a country.
    this.querySelectorAll('[name=country] option').forEach(option => { if (option.value === '---') option.disabled = true; });
    this.updateProvinces();
    this.busy = false;
    this.shipping = null;
    this.carrierPreference = 'colissimo';
    this.shippingLogos = JSON.parse(this.querySelector('[data-shipping-logos]').textContent);
    this.shippingVersion = 0;
    this.addressKey = '';
    this.cartKnown = true;
    this.money = new Intl.NumberFormat(this.dataset.locale, { style: 'currency', currency: typeof this.cart.currency === 'string' ? this.cart.currency : this.dataset.currency });
    this.addEventListener('click', (event) => {
      const quantity = event.target.closest('[data-quantity]');
      if (quantity) this.changeQuantity(quantity.closest('[data-key]').dataset.key, Number(quantity.dataset.quantity), quantity);
      if (event.target.closest('[data-refresh]')) this.refresh();
      if (event.target.closest('[data-shipping-retry]')) { this.addressKey = ''; this.updateShipping(); }
    });
    this.form.addEventListener('input', (event) => {
      if (event.target.name === 'country') this.updateProvinces();
      this.updateShipping(); this.updateCTA();
    });
    this.form.addEventListener('change', (event) => {
      if (event.target.name === 'country') this.updateProvinces();
      if (event.target.name === 'payment') this.updatePayment();
      this.updateShipping(); this.updateCTA();
    });
    this.form.addEventListener('submit', (event) => { event.preventDefault(); this.continuePayment(); });
    this.unsubscribe = typeof subscribe === 'function' ? subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source !== 'elix-order') {
        if (this.busy) this.pendingRefresh = true;
        else this.refresh();
      }
    }) : null;
    this.updatePayment();
    this.updateShipping();
    this.refresh();
  }
  disconnectedCallback() { this.unsubscribe?.(); this.shippingVersion++; clearTimeout(this.shippingTimer); }
  text(selector, value) { this.querySelector(selector).textContent = value; }
  format(cents) { return this.money.format(cents / 100); }
  status(message, error = false) {
    this.text('[data-cart-status]', message);
    this.querySelector('[data-cart-status]').classList.toggle('is-error', error);
  }
  setBusy(value) {
    this.busy = value;
    this.setAttribute('aria-busy', String(value));
    this.querySelectorAll('[data-quantity], [data-refresh]').forEach((button) => { button.disabled = value; });
    this.updateCTA();
    if (!value && this.pendingRefresh) { this.pendingRefresh = false; this.refresh(); }
  }
  async request(url, options = {}) {
    const response = await fetch(url, options);
    const data = await response.json();
    if (!response.ok || data.errors) throw new Error(typeof data.description === 'string' ? data.description : this.strings.error);
    return data;
  }
  async changeQuantity(key, quantity, trigger) {
    if (this.busy || !Number.isInteger(quantity) || quantity < 0) return;
    const action = trigger.classList.contains('order-remove') ? 'remove' : quantity < this.cart.items.find(i => i.key === key)?.quantity ? 'minus' : 'plus';
    this.setBusy(true); this.status(this.strings.loading);
    try {
      const cart = await this.request(routes.cart_change_url, {
        ...fetchConfig(), body: JSON.stringify({ id: key, quantity, sections: [this.dataset.section, 'cart-icon-bubble'], sections_url: `${location.pathname}${location.search}` }),
      });
      this.applyCart(cart);
      const line = Array.from(this.querySelectorAll('[data-key]')).find(el => el.dataset.key === key);
      (line?.querySelector(action === 'minus' ? '[data-quantity]' : '[data-quantity]:nth-of-type(2)') || this.querySelector('[data-refresh]')).focus();
      if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'elix-order', cartData: cart });
      this.status(this.strings.updated);
    } catch (error) {
      // A failed response may follow a committed mutation: require a refresh before continuation.
      this.cartKnown = false; this.status(error.message || this.strings.error, true);
    } finally { this.setBusy(false); }
  }
  async refresh() {
    if (this.busy) return;
    this.setBusy(true);
    try {
      const cart = await this.request(`${routes.cart_url}.js`);
      const sectionURL = new URL(location.href);
      sectionURL.searchParams.set('sections', `${this.dataset.section},cart-icon-bubble`);
      const sectionResponse = await fetch(sectionURL.pathname + sectionURL.search);
      if (!sectionResponse.ok) throw new Error(this.strings.error);
      cart.sections = await sectionResponse.json();
      this.applyCart(cart); this.status(this.strings.updated);
    } catch (error) { this.cartKnown = false; this.status(this.strings.error, true); }
    finally { this.setBusy(false); }
  }
  applyCart(cart) {
    const html = cart.sections?.[this.dataset.section];
    if (!html) throw new Error(this.strings.error);
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const lines = parsed.querySelector('[data-order-lines]');
    if (!lines) throw new Error(this.strings.error);
    this.querySelector('[data-order-lines]').replaceChildren(...Array.from(lines.childNodes));
    const discounts = parsed.querySelector('[data-cart-discounts]');
    this.querySelector('[data-cart-discounts]').replaceChildren(...Array.from(discounts.childNodes));
    const bubble = cart.sections['cart-icon-bubble'];
    if (bubble) {
      const target = document.getElementById('cart-icon-bubble');
      const source = new DOMParser().parseFromString(bubble, 'text/html').querySelector('.shopify-section');
      if (target && source) target.innerHTML = source.innerHTML;
    }
    this.querySelector('elix-secure-order')?.invalidate();
    this.cart = cart;
    this.cartKnown = true;
    this.money = new Intl.NumberFormat(this.dataset.locale, { style: 'currency', currency: cart.currency || this.dataset.currency });
    this.querySelector('[data-empty]').hidden = cart.item_count > 0;
    this.form.hidden = cart.item_count === 0;
    // Cart quantities/discounts may change shipping eligibility. Always invalidate the quote.
    this.addressKey = ''; this.updateShipping(); this.updateTotals(); this.updateCTA();
  }
  address() {
    const values = new FormData(this.form);
    return Object.fromEntries(['country', 'first_name', 'last_name', 'address1', 'address2', 'zip', 'city', 'province'].map(key => [key, String(values.get(key) || '').trim()]));
  }
  addressComplete(address) {
    return address.country !== '---' && ['country', 'first_name', 'last_name', 'address1', 'zip', 'city'].every(key => address[key]) && (this.querySelector('[name=province]').required ? !!address.province : true);
  }
  updateProvinces() {
    const country = this.querySelector('[name=country]');
    if (this.provinceCountry === country.value) return;
    this.provinceCountry = country.value;
    const option = country.selectedOptions[0];
    let provinces = [];
    try { provinces = JSON.parse(option?.dataset.provinces || '[]'); } catch (_) { /* Shopify supplies this data. */ }
    const select = this.querySelector('[name=province]');
    select.replaceChildren(new Option('', ''));
    provinces.forEach(([value, label]) => select.add(new Option(label, value)));
    select.required = provinces.length > 0;
    this.querySelector('[data-province-field]').hidden = !provinces.length;
  }
  updateShipping() {
    const address = this.address();
    const key = JSON.stringify({ address, requiresProvince: this.querySelector('[name=province]').required });
    if (key === this.addressKey) return;
    this.addressKey = key;
    const version = ++this.shippingVersion;
    this.shipping = null;
    this.querySelector('[data-shipping-options]').replaceChildren();
    this.querySelector('[data-shipping-retry]').hidden = true;
    this.text('[data-payment-status]', '');
    this.updateTotals(); this.updateCTA();
    clearTimeout(this.shippingTimer);
    const adapter = window.ElixOrderAdapters.shipping;
    const complete = this.addressComplete(address);
    if (adapter !== elixStandardShipping && (!address.country || address.country === '---' || (adapter?.requiresAddress !== false && !complete))) {
      this.text('[data-shipping-status]', this.strings.address_needed); return;
    }
    if (typeof adapter !== 'function') { this.text('[data-shipping-status]', this.strings.shipping_pending); return; }
    const showError = () => {
      if (version !== this.shippingVersion || !this.isConnected) return;
      this.shipping = null; this.updateTotals(); this.updateCTA();
      this.text('[data-shipping-status]', this.cart.currency === 'EUR' ? this.strings.shipping_error : this.strings.shipping_currency);
      this.querySelector('[data-shipping-retry]').hidden = false;
    };
    const receiveRates = (rates) => {
      if (version !== this.shippingVersion || !this.isConnected) return;
      const preview = adapter === elixStandardShipping && (!address.country || address.country === '---');
      if (!Array.isArray(rates) || !rates.length || rates.some(rate => !rate.id || !rate.label || (!preview && (!Number.isSafeInteger(rate.price) || rate.price < 0)) || rate.currency !== this.cart.currency)) throw new Error('Invalid shipping quote');
      this.text('[data-shipping-status]', preview ? this.strings.carrier_country_hint : complete ? this.strings.shipping_auto : this.strings.address_needed);
      const container = this.querySelector('[data-shipping-options]');
      container.replaceChildren();
      rates.forEach(rate => {
        const label = document.createElement('label'); label.className = 'order-carrier';
        const input = document.createElement('input'); input.type = 'radio'; input.name = 'shipping'; input.value = rate.id; input.required = true;
        input.checked = rate.id === this.carrierPreference || rates.length === 1;
        label.classList.toggle('is-selected', input.checked);
        if (input.checked && !preview) this.shipping = rate;
        if (rate.carrier && this.shippingLogos[rate.carrier]) {
          const logo = document.createElement('img'); logo.className = 'order-carrier-logo'; logo.src = this.shippingLogos[rate.carrier]; logo.alt = ''; logo.width = 108; logo.height = 44;
          label.append(input, logo);
        } else label.append(input);
        const copy = document.createElement('span'); copy.className = 'order-carrier-copy';
        const title = document.createElement('strong'); title.textContent = rate.label; copy.append(title);
        if (rate.detail) { const detail = document.createElement('span'); detail.textContent = rate.detail; copy.append(detail); }
        const amount = document.createElement('span'); amount.className = 'order-carrier-price';
        amount.textContent = preview ? this.strings.carrier_from.replace('{price}', this.format(499)) : this.format(rate.price);
        input.addEventListener('change', () => {
          if (!input.checked) return;
          this.carrierPreference = rate.id; this.shipping = preview ? null : rate;
          container.querySelectorAll('.order-carrier').forEach(card => card.classList.toggle('is-selected', card.querySelector('input').checked));
          this.updateTotals(); this.updateCTA();
        });
        label.append(copy, amount); container.append(label);
      });
      this.updateTotals(); this.updateCTA();
    };
    const load = () => {
      try {
        const result = adapter({ address, cart: this.cart, strings: this.strings });
        if (result && typeof result.then === 'function') result.then(receiveRates).catch(showError);
        else receiveRates(result);
      } catch (_) { showError(); }
    };
    this.text('[data-shipping-status]', this.strings.shipping_loading);
    if (adapter.requiresAddress === false) load();
    else this.shippingTimer = setTimeout(load, 450);
  }
  total() { return this.cart.total_price + (this.shipping?.price || 0); }
  updateTotals() {
    this.text('[data-subtotal]', this.format(this.cart.items_subtotal_price));
    this.text('[data-total]', this.format(this.total()));
    this.text('[data-shipping-total]', this.shipping ? this.format(this.shipping.price) : this.strings.unselected);
  }
  updatePayment() {
    const selected = this.form.elements.payment.value;
    this.querySelectorAll('[data-method]').forEach(card => {
      const active = card.dataset.method === selected;
      card.classList.toggle('is-selected', active);
      card.querySelector('.order-method-panel').hidden = !active;
      card.querySelector('input').setAttribute('aria-expanded', String(active));
    });
    this.text('[data-payment-status]', ''); this.updateCTA();
  }
  eligible() {
    return !this.busy && this.cartKnown && this.cart.item_count > 0 && !!this.shipping && this.addressComplete(this.address()) && this.form.checkValidity();
  }
  updateCTA() {
    const button = this.querySelector('[data-continue]');
    const method = this.form.elements.payment.value;
    const secure = this.querySelector('elix-secure-order');
    button.textContent = secure?.config ? (secure.quoteValid() ? `${secure.s.finalize_order} — ${this.format(secure.quote.total)}` : secure.s.finalize_order) : `${this.strings[`cta_${method}`]} — ${this.format(this.total())}`;
    button.disabled = secure ? (!this.cartKnown || !this.cart.item_count || this.busy || !secure.config || secure.submitting || secure.calculating || secure.unknownOrder) : !this.eligible();
    this.querySelector('[data-requirements]').hidden = !button.disabled;
  }
  async continuePayment() {
    if (!this.form.reportValidity()) return;
    if (!this.eligible()) { this.text('[data-payment-status]', this.strings.address_needed); return; }
    const secure = this.querySelector('elix-secure-order');
    if (secure) { if (!secure.quoteValid()) await secure.calculate(); if (secure.quoteValid()) await secure.submitOrder(); return; }
    this.setBusy(true);
    try {
      const method = this.form.elements.payment.value;
      // Future handlers must revalidate the cart, quote and customer data on a server.
      const result = await window.ElixOrderAdapters.payments[method]({ method, cart: this.cart, shipping: this.shipping, customer: Object.fromEntries(new FormData(this.form)), total: this.total() });
      if (result?.pending) this.text('[data-payment-status]', this.strings.preview);
    } catch (_) { this.text('[data-payment-status]', this.strings.error); }
    finally { this.setBusy(false); }
  }
}
if (!customElements.get('elix-order')) customElements.define('elix-order', ElixOrder);
